/**
 * Refactor by liuxiangyang on 2025/5/20.
 */
import RcPicker from '@tinper/rc-picker';
import momentGenerateConfig from '@tinper/rc-picker/lib/generate/moment';
import type { Locale, PanelMode } from '@tinper/rc-picker/lib/interface';
import classnames from 'classnames';
import type { Moment } from 'moment';
import moment from 'moment';
import omit from 'omit.js';
import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
    cssUtil,
    formatUtils,
    getChildrenText,
    getNid,
    KeyCode,
    prefix,
    setComponentClass,
    timezoneAdaptor,
    Warning
} from '../../wui-core/src';
import { useId } from '../../wui-core/src/reactCompat';
import Icon from '../../wui-icon/src';
import { globalConfig } from '../../wui-provider/src';
import { autoFormat, canFix, deleteFormat, deleteTailSeperator, findMatchIndexs, getDefaultFormat } from './_utils/autoFix';
import { getHalfYear, getHalfYearArr, halfYearFormat } from './_utils/halfYearUtils';
import { getOptions } from './_utils/headerSelect';
import { getLangInfo, getMomentConfig } from './_utils/lang';
import { getPlacement } from './_utils/placement';
import { getActivePresetLabel, getPresets } from './_utils/presets';
import type { DatePickerProps, DatePickerState } from './iPicker';


const {isShouldUpdate /* 废弃API调用时提示更新 */} = Warning

export const _PREFIX_CLS = `${prefix}-picker`
const pickers = ['time', 'date', 'week', 'month', 'year']

// 需包装处理或不传给rc层的 props
const _handleProps: (keyof DatePickerProps)[] = [
    'browser',
    'autoFix',
    'clsPrefix',
    'className',
    'transitionName',
    'size',
    'bordered',
    'getPopupContainer',
    'locale',
    'weekStartsOn',
    'timezone',
    'serverTimezone',
    'enableTimezone',
    'open',
    'disabledDateValid',
    'defaultOpen',
    'defaultPanelShown',
    'defaultValue',
    'dropdownAlign',
    'dropdownClassName',
    'popupClassName',
    'placement',
    'showNow',
    'showTime',
    'showHour',
    'showMinute',
    'showSecond',
    'use12Hours',
    'enterKeyDown',
    'onOpenChange',
    'onKeyDown',
    'outInputKeydown',
    'onBlur',
    'onDateInputBlur',
    'onFocus',
    'onClick',
    'outInputFocus',
    'onChange',
    'onOk',
    'onSelect',
    'dateRender',
    'dateCellRender',
    'monthCellRender',
    'monthCellContentRender',
    'clearIcon',
    'closeIcon',
    'suffixIcon',
    'iconClick',
    'dir'
]

const defaultGetPrefixCls = (suffixCls: string, customizePrefixCls?: string) => {
    if (customizePrefixCls) return customizePrefixCls;
    return suffixCls ? `${prefix}-${suffixCls}` : prefix;
};

export const configContext = React.createContext({
    // We provide a default function for Context without provider
    getPrefixCls: defaultGetPrefixCls,
    renderEmpty: null
});

const initWw = (format: DatePickerProps['format']): boolean => {
    return !!format && /ww|w(?!o)/gi.test(Array.isArray(format) ? format?.[0] : format) // 匹配ww和w，不匹配wo
}

const initOpen = (props: DatePickerProps) => {
    const {open, defaultOpen, defaultPanelShown} = props
    return open ?? defaultOpen ?? defaultPanelShown ?? false
}


const generateClearIcon = (props: DatePickerProps): ReactNode => {
    // closeIcon更新为clearIcon，兼容函数式写法
    const {clearIcon, closeIcon, id, fieldid} = props
    return (
        <span id={id ? id + '_clear' : undefined} fieldid={fieldid ? fieldid + '_clear' : undefined}>
            {(typeof clearIcon === 'function' && clearIcon()) ||
                (typeof closeIcon === 'function' && closeIcon()) ||
                (closeIcon as ReactNode) ||
                (clearIcon as ReactNode)}
        </span>
    )
}

const _dateRender = (currentDate: moment.Moment, today: moment.Moment, {dateRender, dateCellRender, fieldid}: DatePickerProps) => {
    fieldid = fieldid && `${fieldid}_inner-${currentDate.format('YYYY-MM-DD')}`
    if (dateRender) {
        return (
            // 包装一层解决 dateRender 自定义配置样式丢失问题
            <div className={`${_PREFIX_CLS}-cell-inner`} fieldid={fieldid}>
                {dateRender(currentDate, today)}
            </div>
        )
    } else if (dateCellRender) {
        return (
            // 包装一层解决 dateCellRender 自定义配置样式丢失问题
            <div className={`${_PREFIX_CLS}-cell-inner`} fieldid={fieldid}>
                {dateCellRender(currentDate, today)}
            </div>
        )
    } else {
        return (
            // 默认日期样式
            <div className={`${_PREFIX_CLS}-cell-inner`} fieldid={fieldid}>
                {currentDate.get('date')}
            </div>
        )
    }
}

const DatePicker = React.forwardRef<any, DatePickerProps>(
    (props: DatePickerProps) => {
        const pickerRef = useRef<any>(null);
        let yearUl = useRef<HTMLDivElement | null>(null);
        let monthUl = useRef<HTMLDivElement | null>(null);

        const { browser, readOnly, iconClick, disabledDate, picker, timezone, serverTimezone, getPopupContainer, getCalendarContainer, disabledDateValid, isHeaderSelect, showHour, showMinute, showSecond, showNow, renderIcon, locale, dateRender, dateCellRender, fieldid, monthCellRender, weekStartsOn, enterKeyDown, onPanelChange, onOk, onSelect, onChange, onBlur, onDateInputBlur, onKeyDown, outInputKeydown, onOpenChange} = props

        let {
            enableTimezone = true,
            use12Hours,
            className,
            size,
            requiredStyle,
            bordered,
            align,
            placeholder,
            superPrevIcon,
            superNextIcon,
            prevIcon,
            nextIcon,
            dropdownClassName,
            popupClassName,
            dir: direction
        } = props

        if (timezone === 'Asia/Shanghai' && serverTimezone === 'Asia/Shanghai') {
            enableTimezone = false
        }

        const rangeKey = useId();


        /* ---------- ⬇️⬇️⬇️⬇️ state状态 start ⬇️⬇️⬇️⬇️---------- */
        const dropdownAlign = useMemo(() => getPlacement(props), [props.placement, props.dropdownAlign]);
        const [initFormat, setInitFormat] = useState('');
        const [format, setFormat] = useState(initWw(props.format) ? initFormat : initFormat.replace(/ww/gi, 'wo'));
        const [_isFix, setIsFix] = useState(canFix(props, initFormat));
        const [lang, setLang] = useState(getLangInfo(props));
        const [open, setOpen] = useState(initOpen(props));
        const [maskShow, setMaskShow] = useState(false);
        const [_forbidOpen, setForbidOpen] = useState(false);
        const [selectYear, setSelectYear] = useState(0);
        const [selectMonth, setSelectMonth] = useState(0);
        const [activeYearNum, setActiveYearNum] = useState(0);
        const [activeMonthNum, setActiveMonthNum] = useState(0);
        const [diffValue, setDiffValue] = useState([0, 0]);

        /* ---------- ⬇️⬇️⬇️⬇️ 函数（不依赖其他函数）逻辑 start ⬇️⬇️⬇️⬇️---------- */
        const generateFormat = (customFormat?: string | string[]) => {
            let {format, picker = '', showTime} = props

            const globalDataFormat = globalConfig().getGlobalDataFormat()
            // format接入工作台首选项等provider配置
            if (!format && (!picker || picker === 'date') && globalDataFormat) {
                const {dateTimeFormat, dateFormat} = globalDataFormat
                format = formatUtils.diworkFormat2Picker(
                    showTime ? dateTimeFormat : dateFormat,
                    showTime ? 'datetime' : 'date'
                ).format
            }
            // 数组format来源于用户设置的首选项，其中第0项是用户选中值，故format强制取[0]，无需根据list顺序匹配
            customFormat = Array.isArray(customFormat) ? customFormat[0] : customFormat // 兼容string、array类型format
            let mergeFormat = Array.isArray(format) ? format[0] : format // 兼容string、array类型format
            if (picker === 'week' && (customFormat || mergeFormat)) {
                /**
                 * YYYY 格式会导致1月1号等日期所在周年错误，需替换为ISO标准的 g 周年；
                 * 如：2023-01-01 在YYYY-ww 格式下错误输出 2023-52，
                 *                 gggg-ww 格式下正确输出 2023-01
                 */
                customFormat = customFormat?.replace(/y/gi, 'g')
                mergeFormat = mergeFormat?.replace(/y/gi, 'g')
            }
            const _result =
                customFormat ||
                mergeFormat ||
                getDefaultFormat(pickers.includes(picker) ? picker : 'date', showTime, props)
            return initWw(format) ? _result : _result.replace(/W/g, 'w').replace('wo', 'ww')
        }

        const _getFormatStartDate = ({showTime, picker, value, format: customFormat, isStartOfDate = true}: DatePickerProps & {value: Moment; isStartOfDate?: boolean}) => {
            picker = picker || 'date'
            const format = generateFormat(customFormat as string)
            if (!value) return null
            if (picker === 'halfYear') {
                return getHalfYear(value, getLangInfo(props)).value
            } else if (picker === 'week' && moment.isMoment(value)) {
                // 修复QDJCJS-7764：picker===week时，moment.format会将周格式化为该周第一天问题
                return value
            } else if (!showTime && picker === 'date') {
                return isStartOfDate ? moment(value, format).startOf('date') : moment(value, format)
            } else if (typeof showTime === 'object') {
                // 防止传入moment不符合步长
                if (!moment.isMoment(value)) {
                    value = moment(value)
                } // value为moment时无需处理
                let {hourStep, minuteStep, secondStep} = showTime
                hourStep = hourStep ? +hourStep : 1
                minuteStep = minuteStep! ? +minuteStep : 1
                secondStep = secondStep! ? +secondStep : 1
                let hour = value.get('h'),
                    min = value.get('m'),
                    second = value.get('s')
                if (hour % hourStep !== 0) {
                    hour = Math.floor(hour / hourStep) * hourStep
                }
                if (min % minuteStep !== 0) {
                    min = Math.floor(min / minuteStep) * minuteStep
                }
                if (second % secondStep !== 0) {
                    second = Math.floor(second / secondStep) * secondStep
                }
                value.set({hour, minute: min, second})
                return value
            } else {
                // format后的string构造出来的moment除string外的其他单位都是0；
                // 如 moment('2023', 'YYYY')构造结果除年外，月日时分秒均为0；
                return moment(moment.isMoment(value) ? value.format(format) : value, format)
            }
        }

        const getShowTimeOptions = (props: DatePickerProps) => {
            let {format: customFormat, showTime} = props
            if (!showTime) return false
            const format = generateFormat(customFormat as string)

            const globalDataFormat = globalConfig().getGlobalDataFormat()
            // showTime接入工作台首选项等provider配置
            if ((!picker || picker === 'date') && globalDataFormat) {
                const {dateTimeFormat} = globalDataFormat
                const diworkFormat = formatUtils.diworkFormat2Picker(dateTimeFormat, showTime ? 'datetime' : 'date')
                showTime = {...diworkFormat.showTime, ...(typeof showTime === 'object' ? showTime : {})}
            }
            let showTimeOptions: DatePickerProps['showTime'] = {},
                zeroTime = moment('00:00:00', 'HH:mm:ss')
            if (typeof showTime === 'object') {
                // showTime.defaultValue如非moment，则转换为moment
                let {defaultValue, format, use12Hours, hourStep, minuteStep, secondStep} = showTime
                // 为防止用户传入step为0，导致除法报错，不能使用默认值1，需赋值
                hourStep = hourStep ? +hourStep : 1
                minuteStep = minuteStep! ? +minuteStep : 1
                secondStep = secondStep! ? +secondStep : 1
                if (!format) {
                    format = use12Hours ? 'hh:mm:ss a' : 'HH:mm:ss'
                }
                if (defaultValue) {
                    if (typeof defaultValue === 'string') {
                        if (moment(defaultValue, format).isValid()) {
                            defaultValue = moment(defaultValue, format)
                        } else if (!moment(defaultValue, format).isValid()) {
                            defaultValue = zeroTime
                        }
                    } else if (!defaultValue?.isValid?.()) {
                        defaultValue = zeroTime
                    }
                } else {
                    defaultValue = zeroTime // 默认时分秒为 0点0分0秒
                }
                showTimeOptions = {...showTime, defaultValue, hourStep, minuteStep, secondStep}
            } else {
                // 兼容 TimePickerPanel 配置
                showTimeOptions = {
                    format,
                    showHour,
                    showMinute,
                    showSecond,
                    defaultValue: zeroTime // 默认时分秒为 0点0分0秒
                }
            }
            if (showTimeOptions?.use12Hours) {
                showTimeOptions.format = showTimeOptions.format ? showTimeOptions.format.replace(/H/gi, 'h') : 'hh:mm:ss a'
            }
            return showTimeOptions
        }

        // 获取格式后的日期字符串
        const getValue = (value: moment.Moment | string) => {
            // 此处value可能为string或Moment或空
            if (enableTimezone && moment.isMoment(value) && showTime && (!picker || picker === 'date')) {
                const {serverUTC, deviceUTC} = timezoneAdaptor({value, format, timezone, serverTimezone})
                value = value.clone().add(serverUTC.secondDiff - deviceUTC.secondDiff, 's') // 用于回调，需要校正为serverTimezone时区时间
            }
            return moment.isMoment(value) ? value.format(initFormat) : value
        }

        const handleIconClick = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
            e.stopPropagation()
            setForbidOpen(false)
            setOpen(true)
            iconClick?.(e)
        }

        const generateSuffixIcon = (props: DatePickerProps): ReactNode => {
            // 兼顾 iconClick处理
            const {suffixIcon, renderIcon, id, fieldid} = props
            return (
                <span
                    id={id ? id + '_suffix' : undefined}
                    fieldid={fieldid ? fieldid + '_suffix' : undefined}
                    onClick={handleIconClick}
                >
                    {(typeof suffixIcon === 'function' && suffixIcon()) ||
                        (suffixIcon as ReactNode) ||
                        (typeof renderIcon === 'function' && renderIcon()) ||
                        (renderIcon as ReactNode)}
                </span>
            )
        }

        const handleDisabledDate = (current: moment.Moment) => {
            if (!disabledDate) return false
            if (!picker || picker === 'date') {
                if (!showTime) {
                    current = current?.startOf('date')
                } else if (enableTimezone) {
                    current = timezoneAdaptor({
                        value: current,
                        format,
                        timezone,
                        serverTimezone
                    }).value
                }
            }
            return disabledDate?.(current)
        }

        const getPopupContainerDom = (dom: HTMLElement) => {
            if (typeof getPopupContainer === 'function') {
                return getPopupContainer(dom)
            } else if (typeof getCalendarContainer === 'function') {
                return getCalendarContainer(dom) // 兼容旧版本
            } else {
                return cssUtil.parentsUntil(dom)
            }
        }

        /* ---------- ⬇️⬇️⬇️⬇️ 函数逻辑 start ⬇️⬇️⬇️⬇️---------- */
        const initValue = useCallback(({
            timezone,
            serverTimezone,
            value,
            showTime,
            picker,
            locale,
            weekStartsOn,
            format: customFormat,
            disabledDateValid = true,
            disabledDate,
            onChange
        }: DatePickerProps) => {
            const format = generateFormat(customFormat as string)
            if (value) {
                if (typeof value === 'string') {
                    if (moment(value, format).isValid()) {
                        if (!picker || picker === 'date') {
                            if (showTime) {
                                if (enableTimezone) {
                                    // 仅showTime时转换时区
                                    const {diworkUTC, serverUTC} = timezoneAdaptor({
                                        value,
                                        format,
                                        timezone,
                                        serverTimezone
                                    })
                                    value = moment(value, format).add(diworkUTC.secondDiff - serverUTC.secondDiff, 's')
                                } else {
                                    value = moment(moment(value, format).format(format), format)
                                }
                            } else {
                                value = moment(moment(value, format).format(format), format).startOf('date')
                            }
                        } else if (picker === 'halfYear') {
                            value = getHalfYear(value, getLangInfo({locale, weekStartsOn})).value
                        } else {
                            value = moment(moment(value, format).format(format), format)
                        }
                    } else {
                        console.error('value is not in the correct format string --- initValue : ', value, format)
                        value = null
                    }
                } else if (value?.isValid?.()) {
                    // nothing to do
                    if (enableTimezone && showTime && (!picker || picker === 'date')) {
                        value = timezoneAdaptor({value: value!, format, timezone, serverTimezone, isCbValue: false}).value
                    }
                    value = _getFormatStartDate({showTime, picker, value, isStartOfDate: false})
                } else {
                    console.error('value is not in the correct format moment --- initValue : ', value, format)
                    value = null
                }
            }

            if (!disabledDateValid && value) {
                let isDisableDate = disabledDate?.(value as Moment) // 此处value已经过时区转换，无需再处理
                if (isDisableDate) {
                    onChange?.(null, '')
                    return null
                }
            }

            // 确保value的moment实例多语版本一致
            if (value && typeof value !== 'string' /*  && value?.locale */) {
                let langInfo = getLangInfo({locale, weekStartsOn}) // 获取语言包信息
                moment.locale(langInfo.lang, getMomentConfig(weekStartsOn))
                value = moment(value, format)
            }
            return value
        }, []);

        /* ---------- ⬇️⬇️⬇️⬇️ 原state逻辑依赖函数，需调整到函数声明后 start ⬇️⬇️⬇️⬇️---------- */
        const [showTime, setShowTime] = useState(getShowTimeOptions(props));
        const [defaultValue, setDefaultValue] = useState(initValue({...props, value: props.defaultValue}));

        const [value, setValue] = useState(initValue(props));
        const [suffixIcon, setSuffixIcon] = useState(generateSuffixIcon(props));
        const clearIcon = generateClearIcon(props);
        const options = useMemo(() => getOptions(picker, direction), [picker, direction]);

        /* ---------- ⬇️⬇️⬇️⬇️ 回调函数 start ⬇️⬇️⬇️⬇️---------- */
        const handleDateRender = (currentDate: moment.Moment, today: moment.Moment) => {
            return _dateRender(currentDate, today, {dateRender, dateCellRender, fieldid})
        }

        const handleMonthCellRender = (currentDate: moment.Moment, locale: Locale) => {
            if (monthCellRender) {
                return (
                    // 包装一层解决 monthCellRender 自定义配置样式丢失问题
                    <div className={`${_PREFIX_CLS}-cell-inner`}>{monthCellRender?.(currentDate, locale)}</div>
                )
            }
        }

        // 选择cascader之后，联动日期面板
        const onCascaderChange = (val: any) => {
            let differenceYear = 0
            let differenceMonth = 0

            if (!picker || picker === 'date' || picker === 'week') {
                differenceYear = parseInt(val[0]) - selectYear
                differenceMonth = parseInt(val[1]) - selectMonth
            } else if (picker === 'month' || picker === 'quarter' || picker === 'halfYear') {
                differenceYear = parseInt(val[0]) - selectYear
                differenceMonth = 0
            } else if (picker === 'year') {
                let currentYear = selectYear - parseInt(selectYear.toString().slice(3, 4))
                differenceYear = (parseInt(val[0].slice(0, 4)) - currentYear) / 10
                differenceMonth = 0
            }
            setDiffValue([differenceYear, differenceMonth]) // 联动选择值之后让日历面板跳转到相应的年月，传入@tinper/rc-picker
        }

        // bug: 选择年月后关闭年月下拉面板，不选择日期，再点开年月，选择年月，该操作第二次之后header年月显示错误
        // 点击年份
        const onChangeYear = (val: string, index: number) => {
            if (!picker || picker === 'date' || picker === 'week') {
                setSelectYear(parseInt(val))
            } else if (picker === 'month' || picker === 'quarter' || picker === 'halfYear') {
                setMaskShow(false)
                setSelectYear(parseInt(val))
            } else if (picker === 'year') {
                setMaskShow(false)
                setSelectYear(parseInt(val))
            }
            setActiveYearNum(index)
            let newArr = [val, selectMonth]
            onCascaderChange(newArr)
        }

        // 点击月份
        // @ts-ignore
        const onChangeMonth = (monthValue: string, index: number) => {
            setMaskShow(false)
            setSelectMonth(index + 1)
            setActiveMonthNum(index)
            let newArr = [selectYear, index + 1]
            onCascaderChange(newArr)
        }

        const initSelectValue = useCallback(() => {
            // 初始化下拉默认值
            let {value} = props
            if (!isHeaderSelect || !open) false
            let selectYear = 0
            let selectMonth = 0
            let dateValue = value || defaultValue
            if (dateValue) {
                if (picker === 'week') {
                    let formatCopy = 'gggg-ww'
                    if (moment(dateValue, formatCopy).isValid()) {
                        let newValue = moment(dateValue, formatCopy)
                        selectYear = moment(newValue).year()
                        selectMonth = moment(newValue).month() + 1
                    } else {
                        console.error(
                            'value is not in the correct format week --- initSelectValue : ',
                            dateValue,
                            formatCopy
                        )
                    }
                } else if (picker === 'halfYear') {
                    const valueMoment = getHalfYear(dateValue, lang).value
                    selectYear = valueMoment!.year()
                    selectMonth = valueMoment!.month() + 1
                } else {
                    if (moment(dateValue).isValid()) {
                        selectYear = moment(dateValue).year()
                        selectMonth = moment(dateValue).month() + 1
                    } else {
                        console.error('value is not in the correct format date --- initSelectValue : ', dateValue)
                    }
                }
            } else {
                selectYear = new Date().getFullYear()
                selectMonth = new Date().getMonth() + 1
            }

            if (!disabledDateValid && dateValue) {
                let isDisableDate = disabledDate?.(moment(dateValue))
                if (isDisableDate) {
                    selectYear = new Date().getFullYear()
                    selectMonth = new Date().getMonth() + 1
                }
            }

            setSelectYear(selectYear)
            setSelectMonth(selectMonth)
        }, [picker, isHeaderSelect, open, disabledDateValid, disabledDate, value, defaultValue])

        const clickValue = (e: any) => {
            setMaskShow(!maskShow)
            let currentYear = parseInt(
                e.target.parentNode.parentNode.querySelector(`.${_PREFIX_CLS}-year-btn`)?.textContent
            )
            let currentMonth = e.target.parentNode.parentNode.querySelector(`.${_PREFIX_CLS}-month-btn`)?.textContent
            options.map((item, index) => {
                if (picker === 'year') {
                    // picker为年时以10年为单位
                    let currentYearRang = e.target.parentNode.parentNode.querySelector(
                        `.${_PREFIX_CLS}-decade-btn`
                    ).textContent
                    if (item.label === currentYearRang) {
                        setActiveYearNum(index)
                    }
                } else {
                    if (parseInt(item.label) === currentYear) {
                        setActiveYearNum(index)
                        setSelectYear(currentYear)
                    }
                }
            })
            moment.locale(lang.lang, getMomentConfig(weekStartsOn))
            let monthArr = lang.langMap?.monthsShort || moment.localeData().monthsShort()
            // @ts-ignore
            monthArr.map((item: any, index: number) => {
                if (currentMonth === item) {
                    setActiveMonthNum(index)
                    setSelectMonth(index + 1)
                }
            })
        }

        const handleHeaderSelect = () => {
            moment.locale(lang.lang, getMomentConfig(weekStartsOn))
            let monthData = lang.langMap?.monthsShort || moment.localeData().monthsShort()
            let node = (
                <div className={`${_PREFIX_CLS}-header-select`}>
                    <div className={`${_PREFIX_CLS}-header-select-year`} ref={yearUl}>
                        <ul>
                            {options.map((item, index) => {
                                return (
                                    <li
                                        className={
                                            activeYearNum == index
                                                ? `${_PREFIX_CLS}-header-select-year-active`
                                                : undefined
                                        }
                                        key={item.value + index}
                                        {...(fieldid ? {fieldid: `${fieldid}_year_${parseInt(item.label)}`} : {})}
                                        onClick={() => onChangeYear(item.label, index)}
                                    >
                                        {lang.lang === 'zh-cn' || lang.lang === 'zh-tw'
                                            ? picker != 'year'
                                                ? activeYearNum == index
                                                    ? parseInt(item.label) + ' 年'
                                                    : parseInt(item.label)
                                                : item.label
                                            : picker != 'year'
                                                ? parseInt(item.label)
                                                : item.label}
                                    </li>
                                )
                            })}
                        </ul>
                    </div>
                    {options[0]?.children && (
                        <div className={`${_PREFIX_CLS}-header-select-month`} ref={monthUl}>
                            <ul>
                                {Array.isArray(monthData) && monthData?.map((item: string, index: number) => {
                                    return (
                                        <li
                                            className={
                                                activeMonthNum == index
                                                    ? `${_PREFIX_CLS}-header-select-month-active`
                                                    : undefined
                                            }
                                            key={item + index}
                                            {...(fieldid ? {fieldid: `${fieldid}_month_${index + 1}`} : {})}
                                            onClick={() => onChangeMonth(item, index)}
                                        >
                                            {lang.lang === 'zh-cn' || lang.lang === 'zh-tw' ? parseInt(item) : item}
                                            {lang.lang === 'zh-cn' || lang.lang === 'zh-tw'
                                                ? activeMonthNum == index
                                                    ? ' 月'
                                                    : ''
                                                : ''}
                                        </li>
                                    )
                                })}
                            </ul>
                        </div>
                    )}
                </div>
            )
            let iconNode = (
                <div className={`${_PREFIX_CLS}-header-select-icon`}>
                    {maskShow ? <Icon type='uf-gridcaretarrowup' /> : <Icon type='uf-gridcaretdown' />}
                </div>
            )
            return (
                <>
                    {iconNode}
                    <div className={`${_PREFIX_CLS}-header-select-box`}>
                        {maskShow && node}
                        <div
                            style={{background: 'transparent', height: '100%'}}
                            onClick={clickValue}
                            {...(fieldid ? {fieldid: `${fieldid}_header-select`} : {})}
                        >
                            {' '}
                        </div>
                    </div>
                </>
            )
        }

        const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
            e.stopPropagation()
            props.onClick?.(e)
        }

        const handlePanelChange = (value: moment.Moment, mode: PanelMode) => {
            // 点击前后按钮时更新下拉value值
            if (enableTimezone && value && showTime && (!picker || picker === 'date')) {
                value = timezoneAdaptor({
                    value,
                    format: initFormat,
                    timezone,
                    serverTimezone
                }).value
            }

            onPanelChange?.(value, mode)
            if (!isHeaderSelect) return
            let year = value.year()
            let month = value.month() + 1
            if (!picker || picker === 'date' || picker === 'week') {
                setSelectYear(year)
                setSelectMonth(month)
            } else if (picker === 'month' || picker === 'quarter' || picker === 'halfYear') {
                setSelectYear(year)
            } else if (picker === 'year') {
                let downRound = value.year() - parseInt(value.year().toString().slice(3, 4))
                setSelectYear(downRound)
            }
        }

        // 确认修改日期
        const handleChange = (value: moment.Moment, dateString: string) => {
            let showTimeValue = _getFormatStartDate({showTime, picker, value, format: initFormat})
            if (enableTimezone && value && showTime && (!picker || picker === 'date')) {
                const {diworkUTC, valueUTC} = timezoneAdaptor({value, format, timezone, serverTimezone})
                showTimeValue =
                    showTimeValue && showTimeValue.clone().subtract(diworkUTC.secondDiff - valueUTC.secondDiff, 's') // 用于回调，需要校正为serverTimezone时区时间
                dateString = (showTimeValue && getValue(showTimeValue)) || ''
            }
            // 对于受控组件，清空操作时也需要立即更新内部 state
            // 避免 blur 事件时内部 state 还是旧值的问题
            if (!('value' in props) || (showTimeValue === null)) {
                setValue(showTimeValue)
            }
            if (picker === 'halfYear') {
                const halfYearArr = getHalfYearArr(lang, dateString)
                onChange?.(showTimeValue, dateString, halfYearArr)
            } else {
                dateString = (showTimeValue && getValue(showTimeValue)) || '' // 输入年份后，直接点击月份时，此时值格式为yyyy-,获取的dateString为不全的值如：2023-，通过这个值在计算只能获取的2023-01-01，所以通过正确的showTimeValue在格式化一下
                onChange?.(showTimeValue, dateString)
            }
        }

        const handleSelect = (selectedValue: moment.Moment) => {
            // halfYear 单独处理回调及相关dom事件
            if (picker === 'halfYear') {
                const {value: valueMoment, dateString} = getHalfYear(selectedValue, lang)
                if (!('value' in props)) {
                    setValue(valueMoment)
                }
                onSelect?.(valueMoment, dateString)
                return
            }

            // 确保用户拿到值为startOf date
            let showTimeValue = _getFormatStartDate({showTime, picker, value: selectedValue})
            // showTime和picker = time时，只有点击'确定'按钮或者'此刻'按钮才赋值（修复showTime时，value修改没有触发onChange事件）
            if (!showTime && picker !== 'time') {
                if (!('value' in props)) {
                    setValue(showTimeValue)
                }
                setFormat(initFormat)
            }

            if (enableTimezone && selectedValue && showTime && (!picker || picker === 'date')) {
                const {diworkUTC, valueUTC} = timezoneAdaptor({value: selectedValue, format: initFormat, timezone, serverTimezone})
                showTimeValue =
                    showTimeValue && showTimeValue.clone().subtract(diworkUTC.secondDiff - valueUTC.secondDiff, 's') // 用于回调，需要校正为serverTimezone时区时间
            }

            onSelect?.(showTimeValue, (showTimeValue && getValue(showTimeValue)) || '')
            // QDJCJS-7925 修复手动输入年份后再弹出面板选择1月1号时不触发onChange问题
            if (selectedValue.diff(value) === 0) {
                onChange?.(showTimeValue, (showTimeValue && getValue(showTimeValue)) ?? '')
            }
        }

        const handleOk = (value: moment.Moment) => {
            let showTimeValue = _getFormatStartDate({showTime, picker, value})

            if (enableTimezone && showTimeValue && (!picker || picker === 'date')) {
                showTimeValue = timezoneAdaptor({
                    value: showTimeValue,
                    format: initFormat,
                    timezone,
                    serverTimezone
                }).value
            }
            if (!('value' in props)) {
                setValue(showTimeValue)
            }
            setFormat(initFormat)

            onOk?.(showTimeValue, (showTimeValue && getValue(showTimeValue)) || '')
        }

        const handleOpenChange = (open: boolean) => {
            // 兼容enterKeyDown禁止打开面板
            if (_forbidOpen && open) return
            const _diffValue = !open ? [0, 0] as DatePickerState['diffValue'] : diffValue // QDJCJS-27388 修复cascaderChange直接赋值diffValue时，用户可能选择了下拉的select后未选择日期直接关闭面板，导致下次打开面板时显示错误年月，所以每次关闭面板时都清空diffValue
            setOpen(open)
            setDiffValue(_diffValue)
            let _value = value

            if (enableTimezone && _value && showTime && (!picker || picker === 'date')) {
                _value = timezoneAdaptor({value: _value, format: initFormat, timezone, serverTimezone, isCbValue: moment.isMoment(_value)}).value
            }

            onOpenChange?.(
                open,
                _value,
                picker === 'halfYear'
                    ? getHalfYear(_value, lang).dateString
                    : moment.isMoment(_value) // eslint-disable-next-line indent
                    ? getValue(_value) // eslint-disable-next-line indent
                    : _value ?? ''
            )
            if (!open) {
                setMaskShow(open)
            } else {
                if (_value) {
                    setDiffValue([0, 0]) // QDJCJS-26813 修复MonthPicker先选择一个月份，再切换年月，年份会每次选择后减N问题
                    setSelectYear(moment(_value).year())
                    setSelectMonth(moment(_value).month() + 1)
                }
            }
        }

        /**
        * 格式化Input输入内容
        *  =========== input输入字符串视为diwork时区字符串（与props字符串视为server时区不同），回调将调整为sercer时区 ============
        */
        const handleInputFormat = (e: any) => {
            let format = initFormat
            // * 开启自动校正
            if (_isFix) {
                let value = e.target.value,
                    isDeleteChanged = false
                if (!/^\d/g.test(value)) { // 非数字开头视为非法输入，不做处理
                    return
                }
                const hasSeparator = /[^YyMmDdHhSsGgWwOoAa]+/g.test(format) // 判断是否有 - / . 年月日、空格等分隔符
                const {joinIcon, addLength} = findMatchIndexs(format, hasSeparator)
                if (hasSeparator && joinIcon?.[0] !== '') { // YYYY和MM之间有分隔符才处理的逻辑
                    if (/^\d{5}$/.test(value)) {
                        // 处理删除尾部补足的分隔符-后再输入数字时只填充分隔符-而没数字问题
                        if (addLength?.[0]) {
                            value = value.slice(0, addLength[0]) + joinIcon[0] + value.slice(addLength[0])
                            isDeleteChanged = true
                        }
                    } else if (value.search(/\d{5,}/) > -1) {
                        // 处理日期中间删除问题
                        value =
                            e.target.value.slice(0, Math.max(0, e.target.selectionStart - 1)) +
                            e.target.value.slice(e.target.selectionStart)
                        isDeleteChanged = true
                    }
                }

                let formatDate = autoFormat({value, format, showTime, locale: lang.lang})
                let valueStr = formatDate.value
                if (e.inputType === 'deleteContentBackward') {
                    if (value.length === 0) {
                        // 删除到空时需触发onChange，并关闭面板触发onOpenChange
                        setOpen(false)
                        setValue(null)
                        setFormat(initFormat)
                        onChange?.(null, '')
                        onOpenChange?.(false, null, '')
                        return
                    }
                    // 删除操作尾部分隔符处理
                    formatDate = Object.assign(formatDate, deleteTailSeperator(valueStr, formatDate.format))
                }

                let valueMoment = moment(valueStr, formatDate.format)
                if (formatDate.isChanged || isDeleteChanged) {
                    if (/\D0$/g.test(valueStr)) {
                        /* 尾部的-0特别处理，format替换为-0 */
                        let tempFormat = formatDate.format.slice(0, -2) + valueStr.slice(-2)
                        setValue(valueStr ? moment(valueStr, tempFormat) : null)
                        setFormat(tempFormat)
                    } else if (!/[^S]+/g.test(formatDate.format)) {
                        /* 只有年的特别处理，Y被转S替换，禁止用户非数字输入 */
                        setValue(valueStr ? moment(valueStr.replace(/\D+/g, ''), formatDate.format) : null)
                        setFormat(formatDate.format)
                    } else {
                        setValue(valueStr ? moment(valueStr, formatDate.format) : null)
                        setFormat(formatDate.format)
                    }
                }
                if (
                // 输入完成
                    format?.length === valueStr?.length && valueMoment.isValid()
                ) {
                    // 先更新状态
                    setOpen(false) // 输入完成需关闭面板，并触发onOpenChange回调
                    if (!disabledDateValid && disabledDate?.(valueMoment)) {
                        // QDJCJS-32076 非法值清空时需触发onChange及onOpenChange
                        if (!('value' in props)) {
                            setValue(null)
                        }
                        setFormat(initFormat)
                        onChange?.(null, '')
                        onOpenChange?.(false, null, '')
                        return
                    }
                    setValue(valueMoment)
                    setFormat(format)

                    // 在状态更新完成后触发回调
                    if (enableTimezone && showTime && (!picker || picker === 'date')) {
                        valueMoment = timezoneAdaptor({
                            value: valueMoment,
                            format,
                            timezone,
                            serverTimezone
                        }).value
                        valueStr = getValue(valueMoment) || ''
                    }
                    onChange?.(valueMoment, valueStr)
                    onOpenChange?.(false, valueMoment, valueStr)
                }
            } else {
                // * 无需自动校正场景，仅保留合法值
                let inputValue = e.target.value
                let valueMoment = moment(inputValue, format)

                // halfYear 单独处理回调及相关dom事件
                if (inputValue.length === format.length && picker === 'halfYear') {
                    requestAnimationFrame(() => {
                        const {value, dateString} = getHalfYear(inputValue, lang)
                        setValue(value)
                        const halfYearArr = getHalfYearArr(lang, dateString)
                        onChange?.(value, dateString, halfYearArr)
                    })
                } else if (inputValue.length === format.length && valueMoment.isValid()) {
                    requestAnimationFrame(() => {
                        setValue(valueMoment)
                        setFormat(format)

                        if (enableTimezone && showTime && (!picker || picker === 'date')) {
                            valueMoment = timezoneAdaptor({
                                value: valueMoment,
                                format,
                                timezone,
                                serverTimezone
                            }).value
                            inputValue = getValue(valueMoment) || ''
                        }
                        onChange?.(valueMoment, inputValue)
                    })
                }
            }
        }

        const handleKeyDown = (e: any) => {
            let _forbidOpen = false
            if (e.keyCode === KeyCode.ESC) {
                setFormat(initFormat)
            } else if (e.keyCode === KeyCode.BACKSPACE) {
                // * 删除拖蓝选中内容
                let s = getSelection() as unknown as string
                let reg = new RegExp(s, 'g'),
                    _value = e.target.value
                if (_value.length === 1) return // * 删除到空不在此处处理
                if (_value.match(reg)?.length === 1) {
                    _value = _value.replace(s.toString(), '')
                }
                s = s.toString()

                // * 开启自动校正
                if (_isFix) {
                    let value = _value
                    if (s.length === 0) {
                        // * backspace直接删除，无拖蓝选中区域，则只删除光标前一个字符
                        value = value.slice(0, e.target.selectionStart - 1) + value.slice(e.target.selectionStart)
                        let yearStr = value.match(/\d{5,}/g)?.[0]
                        if (yearStr && _value[e.target.selectionStart - 1]?.search(/\D+/) > -1) {
                            value =
                                _value.slice(0, Math.max(0, e.target.selectionStart - 2)) +
                                _value.slice(Math.max(0, e.target.selectionStart - 1))
                        }
                    }

                    let formatDate = autoFormat({value, format: initFormat, showTime, locale: lang.lang})
                    /* 删除操作后结尾为分隔符，则把分隔符一同删除 */
                    formatDate = Object.assign(formatDate, deleteTailSeperator(formatDate.value, formatDate.format))

                    if (
                        (formatDate.format === initFormat || formatDate.format === '') && // fix: QDJCJS-9354 合法日期但尚未完成操作时不应执行赋值操作
                        formatDate.value &&
                        formatDate.isChanged &&
                        moment(formatDate.value, formatDate.format).isValid() &&
                        !/S+/g.test(formatDate.format) /* 删除到最后的年不特别处理 */
                    ) {
                        setValue(moment(formatDate.value, formatDate.format))
                        setFormat(formatDate.format)
                    }
                }
            } else if (e.keyCode === KeyCode.ENTER) {
                // enterKeyDown为false时按下enter键不打开面板
                if (!enterKeyDown) {
                    _forbidOpen = true
                }
                let value: moment.Moment | null
                let inputValue = e.target.value
                // * 开启自动校正
                if (_isFix) {
                    if (inputValue.length < initFormat.length && inputValue) {
                        inputValue = deleteFormat(inputValue, initFormat)
                    }
                    const formatDate = autoFormat({value: inputValue, format: initFormat, showTime, locale: lang.lang})
                    let valueStr = formatDate.value
                    value = moment(valueStr, initFormat)
                    if (value.isValid()) {
                        setValue(value)
                        setFormat(initFormat)
                    }

                    if (enableTimezone && value.isValid() && showTime && (!picker || picker === 'date')) {
                        value = timezoneAdaptor({
                            value,
                            format: initFormat,
                            timezone,
                            serverTimezone
                        }).value
                        valueStr = getValue(value) || ''
                    }
                    onChange?.(value, valueStr)
                } else {
                    // halfYear 单独处理回调及相关dom事件
                    if (picker === 'halfYear') {
                        value = getHalfYear(inputValue, lang).value
                        setValue(value)
                        setFormat(initFormat)
                        const halfYearArr = getHalfYearArr(lang, inputValue)
                        onChange?.(value, inputValue, halfYearArr)
                    } else {
                        if (inputValue === '') {
                            value = null
                        } else {
                            value = moment(inputValue, initFormat)
                        }
                        if (value === null || value.isValid()) {
                            setValue(value)
                            setFormat(initFormat)
                        }
                        if (enableTimezone && value?.isValid() && showTime && (!picker || picker === 'date')) {
                            value = timezoneAdaptor({
                                value,
                                format: initFormat,
                                timezone,
                                serverTimezone
                            }).value
                            inputValue = getValue(value) || ''
                        }
                        onChange?.(value, inputValue)
                    }
                }
            }
            setForbidOpen(_forbidOpen)
            onKeyDown?.(e, e.preventDefault) || outInputKeydown?.(e, e.preventDefault)
        }

        const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            if (picker === 'week') {
                setFormat(initFormat?.replace(/wo/gi, 'ww'))
            }
            if (readOnly) return
            if (pickerRef.current) {
                props.open ?? e.stopPropagation()
                pickerRef.current.focus()
                props.onFocus?.(e) || props.outInputFocus?.(e)
                setForbidOpen(false)
            }
        }

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            // React 18 并发模式兼容：如果组件 value 已经是 null（刚清空），直接使用 null
            // 避免 e.target.value 还保留旧值的问题
            let inputValue = e.target.value
            const stateValue = value // 获取当前组件的 state value

            // 如果 state 已经是 null 而 input 还有值，说明是清空操作，应该使用空值
            if (stateValue === null && inputValue) {
                inputValue = ''
            }

            // halfYear 单独处理回调及相关dom事件
            if (picker === 'halfYear') {
                let valueMoment = getHalfYear(inputValue, lang).value
                if (!disabledDateValid && valueMoment && disabledDate?.(valueMoment)) {
                    valueMoment = null
                    inputValue = ''
                }
                if (!('value' in props)) {
                    setValue(valueMoment)
                }

                const halfYearArr = getHalfYearArr(lang, inputValue)
                onChange?.(valueMoment, inputValue, halfYearArr)
                if (pickerRef.current) {
                    pickerRef.current.blur()
                }
                onBlur?.(e, inputValue) || onDateInputBlur?.(e, inputValue)
                return
            }

            let valueMoment = moment(inputValue, initFormat)
            // QDJCJS-10002, moment不能简易传入format里非年月日时分秒的格式，对周 w 单独处理
            if (/w/gi.test(initFormat)) {
                const reg = /[YyMmDdHhSsWwGgOo\d]+/gi
                const formatArr = initFormat.match(reg)
                const valueArr = inputValue.match(reg)
                let weekNum
                formatArr?.forEach((item, i) => {
                    if (/w/gi.test(item)) {
                        weekNum = +valueArr?.[i]!
                        valueMoment = valueMoment.week(weekNum)
                    }
                })
            }
            // 失焦时有效值保存
            const isValidValue = inputValue && valueMoment.isValid()
            let _value = isValidValue ? valueMoment : null
            let _valueString = isValidValue ? valueMoment.format(initFormat) : ''
            if (!disabledDateValid && _value && disabledDate?.(_value)) {
                _value = null
                _valueString = ''
            }

            if (!('value' in props)) {
                setValue(_value)
            }
            const _format = initWw(props.format) ? initFormat : initFormat.replace(/ww/gi, 'wo')
            setFormat(_format)

            if (enableTimezone && _value && showTime && (!picker || picker === 'date')) {
                _value = timezoneAdaptor({
                    value: _value,
                    format: initFormat,
                    timezone,
                    serverTimezone
                }).value
                _valueString = (_value && getValue(_value)) || ''
            }
            if (_valueString !== inputValue || showTime) {
                // 失焦需触发onChange
                onChange?.(_value, _valueString)
            }
            if (pickerRef.current) {
                pickerRef.current.blur()
            }
            onBlur?.(e, _valueString) || onDateInputBlur?.(e, _valueString)
        }

        const value2Str = (value: any, format: any) => {
            let dateString = '--'
            if (value && value.isValid()) {
                if (picker === 'halfYear') {
                    dateString = halfYearFormat(value, lang, true)
                } else {
                    dateString = value.format(format)
                }
            } else {
                dateString = value?.toString()
            }
            const displayText = dateString || '--'; // 如果值为空，显示 '--'
            return displayText
        }

        // 浏览态
        const renderBrowser = (params: any) => {
            let {browserClassName, browserStyle, bordered, value, format} = params;

            const componentCls = classnames(`${_PREFIX_CLS}-browser`, {
                [browserClassName]: browserClassName,
                [`${prefix}-component-browser-border-bottom`]: bordered === 'bottom'
            })

            return (
                <div className={`${prefix}-component-browser`}>
                    <div className={componentCls} style={browserStyle} fieldid={fieldid}>
                        {value2Str(value, format)}
                    </div>
                </div>
            );
        }

        const scrollToActive = useCallback(() => {
            // 每次面板展开选中项在面板中间
            let liDom = document.querySelectorAll(`.${_PREFIX_CLS}-header-select-year li`)[0]
            let liHeight = liDom?.getBoundingClientRect()?.height
            if (yearUl.current && liHeight) {
                yearUl.current.scrollTop = (activeYearNum - 2) * liHeight;
            }
            if (monthUl.current && liHeight) {
                monthUl.current.scrollTop = (activeMonthNum - 2) * liHeight;
            }
        }, [activeYearNum, activeMonthNum])

        /* ---------- ⬇️⬇️⬇️⬇️ 原 render逻辑 start ⬇️⬇️⬇️⬇️---------- */
        const mergedCls = useMemo(() => classnames(className, size && `${_PREFIX_CLS}-${size}`, {
            ...setComponentClass({clsPrefix: _PREFIX_CLS, bordered, align, requiredStyle, readOnly})
        }), [className, size, bordered, align, requiredStyle, readOnly])
        const globalDataFormat = globalConfig().getGlobalDataFormat()
        let now = moment()
        if ((!picker || picker === 'date') && showTime) {
            if (globalDataFormat) {
                const {dateTimeFormat} = globalDataFormat
                use12Hours = use12Hours ?? formatUtils.diworkFormat2Picker(dateTimeFormat, 'datetime').use12Hours
            }
        }
        if (enableTimezone) {
            const {diworkUTC, deviceUTC} = timezoneAdaptor({
                value: now.format(),
                format: 'YYYY-MM-DD HH:mm:ss',
                timezone,
                serverTimezone
            })
            now = now.clone().add(diworkUTC.secondDiff - deviceUTC.secondDiff, 's') // ·此刻·显示为工作台时区
        }
        const generateConfig = useMemo(() => Object.assign(momentGenerateConfig, {getNow: () => now}), [now]) // 工作台与计算机时区不一致场景的时间调整
        const adapterNid = getNid(props)

        // 此处改造为函数，传入now作为moment()
        const presets = getPresets(props.presets, now);
        const [activePresetLabel, setActivePresetLabel] = useState(getActivePresetLabel({...props, presets}));

        /* ---------- ⬇️⬇️⬇️⬇️ 钩子函数 start ⬇️⬇️⬇️⬇️---------- */
        useEffect(() => {
            isShouldUpdate('DatePicker', props);

            const _format = generateFormat(props.format)
            setInitFormat(_format);
            setIsFix(canFix(props, initFormat))

            const _defaultValue = (props.defaultValue && moment(props.defaultValue, _format?.replace(/wo/gi, 'ww'))) ?? undefined
            setDefaultValue(_defaultValue)
        }, [])

        useEffect(() => {
            // QDJCJS-7554 不能增加新值原值不同判断，有业务场景会判断后传入相同的值
            if ('value' in props) {
                const res = initValue(props);
                setValue(res);
            }
        }, [props.value, props.defaultValue, props.format, props.picker, props.locale, props.weekStartsOn]);

        useEffect(() => {
            const {format: nextFormat, open, showTime, suffixIcon, activePresetKey} = props;

            if (typeof open === 'boolean') {
                setOpen(open)
            }
            if (nextFormat) {
                const format = generateFormat(nextFormat);
                setFormat(format)
                setInitFormat(format)
                setIsFix(canFix(props, format))
            }
            if (
                showTime || showTime === false ||
                typeof showHour === 'boolean' ||
                typeof showMinute === 'boolean' ||
                typeof showSecond === 'boolean'
            ) {
                setShowTime(getShowTimeOptions(props))
            }
            if (suffixIcon || renderIcon) {
                setSuffixIcon(generateSuffixIcon(props))
            }
            if (locale) {
                const lang = getLangInfo(props) // 获取语言包信息
                setLang(lang)
            }
            if (activePresetKey) {
                const presets = getPresets(props.presets, now);
                setActivePresetLabel(getActivePresetLabel({...props, presets}))
            }
        }, [props.format, props.open, props.showTime, showHour, showMinute, showSecond, props.suffixIcon, renderIcon, locale, props.activePresetKey, props.presets]);

        /* ---------- ⬇️⬇️⬇️⬇️ 原didMount钩子中操作 start ⬇️⬇️⬇️⬇️---------- */
        useLayoutEffect(() => {
            initSelectValue()
        }, [value, picker, disabledDateValid, disabledDate, isHeaderSelect, open]);

        useLayoutEffect(() => {
            scrollToActive()
        }, [maskShow]);

        if (browser) {
            return renderBrowser({size, className, bordered, align, requiredStyle, value: value !== undefined ? value : defaultValue, format: format || generateFormat(props.format)})
        }

        return (
            <RcPicker
                /* 内置配置 */
                ref={pickerRef}
                generateConfig={generateConfig}
                prefixCls={_PREFIX_CLS}
                // transitionName='slide-up'
                tabIndex={0} //  Tab 按键切换顺序，不暴露给用户
                direction={direction}
                /* custom需omit配置 */
                {...adapterNid}
                {...omit(props, _handleProps)}
                // picker='week'
                /* custom配置 */
                placeholder={getChildrenText(placeholder).join('')}
                defaultValue={defaultValue}
                value={value ? value : value !== undefined ? null : undefined}
                format={picker === 'halfYear' ? (date: Moment) => halfYearFormat(date, lang) : format}
                open={open ?? undefined}
                locale={Object.assign({}, lang.langMap, {
                    shortMonths: lang.langMap.monthsShort,
                    // @ts-ignore
                    shortWeekDays: lang.langMap.weekdaysMin,
                    halfYearFormat: (date: Moment) => halfYearFormat(date, lang, false)
                })}
                dropdownAlign={dropdownAlign}
                dropdownClassName={dropdownClassName || popupClassName}
                className={mergedCls}
                activePresetLabel={activePresetLabel}
                // @ts-ignore
                presets={presets}
                showNow={picker === 'time' ? false : showNow} // picker=time不显示`此刻`按钮, 与TimePicker保持一致
                showTime={showTime}
                use12Hours={use12Hours}
                clearIcon={clearIcon}
                suffixIcon={suffixIcon}
                superPrevIcon={superPrevIcon ?? <Icon type={direction === 'rtl' ? 'uf-youjiantou_shuang' : 'uf-daoshouye'} />} // UE 需求，更换默认翻页箭头
                superNextIcon={superNextIcon ?? <Icon type={direction === 'rtl' ? 'uf-daoshouye' : 'uf-youjiantou_shuang'} />}
                prevIcon={prevIcon ?? <Icon type={direction === 'rtl' ? 'uf-arrow-right' : 'uf-arrow-left'} />}
                nextIcon={nextIcon ?? <Icon type={direction === 'rtl' ? 'uf-arrow-left' : 'uf-arrow-right'} />}
                disabledDate={handleDisabledDate}
                onOpenChange={handleOpenChange}
                onSelect={handleSelect}
                onChange={handleChange}
                onOk={handleOk}
                onInputChange={handleInputFormat}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onClick={handleClick}
                dateRender={handleDateRender}
                monthCellRender={monthCellRender ? handleMonthCellRender : undefined}
                getPopupContainer={getPopupContainerDom}
                onPanelChange={handlePanelChange}
                // @ts-ignore
                diffValue={open && isHeaderSelect && diffValue}
                headerSelect={open && isHeaderSelect ? handleHeaderSelect() : null}
                showSelectMask={open && isHeaderSelect && maskShow}
                domKey={`datepicker${rangeKey}`}
            />
        )
    },
)

const defaultProps = {
    size: 'md' as DatePickerProps['size'],
    // locale: 'zh-cn',
    // transitionName: 'slide-up',
    clearIcon: <Icon type='uf-close-c' />,
    suffixIcon: <Icon type='uf-calendar' />,
    timezone: 'Asia/Shanghai',
    serverTimezone: 'Asia/Shanghai',
    allowClear: true,
    autoFix: true,
    enterKeyDown: true,
    isHeaderSelect: true
}
DatePicker.defaultProps = defaultProps;

DatePicker.displayName = 'DatePicker';

export default DatePicker;
